import { Tooltip } from '@bp/ui-components';
import classNames from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMemorizedCacheTag } from '../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../hooks/usePermissionChecker';
import { useSelectedSchoolYear } from '../../hooks/useSelectedSchoolYear';
import {
  use_SubjectContainersQuery,
  useTeachingBlockCurrentStatusQuery,
} from '../../types/planung-graphql-client-defs';
import { LicenseLock } from '../LicenseHint/LicenseHint';
import { useTeachingBlocks } from '../Teachingblock/hooks/useTeachingBlocks';
import { getTodayPosition } from './getTodayPosition';
import styles from './TimetableBlock.module.scss';
import { TodayMarker } from './TodayMarker';

type SubjectContainerProps = {
  /** `bar` = Zeitleiste (Desktop), `list` = Liste unter den Abschnitten (mobil, mobile-plan.md E6) */
  variant?: 'bar' | 'list';
};

export const SubjectContainer = ({ variant = 'bar' }: SubjectContainerProps) => {
  const { t } = useTranslation();
  const schoolYear = useSelectedSchoolYear();
  const {
    started: todayStarted,
    inRange: todayInRange,
    percent: todayPercent,
  } = getTodayPosition(schoolYear?.start, schoolYear?.end);
  const { teachingBlocks } = useTeachingBlocks(schoolYear?.uuid);
  // Ohne gebuchte Epochenplanung liefert der Server keine Epochenpläne; statt der leeren Zeile der Buchungshinweis.
  const epochenLicensed = usePermissionChecker()?.isEpochenEnabled() ?? true;
  const licenseHint = (
    <span className={styles['license-hint']}>
      <LicenseLock hint={{ kind: 'teachingBlocks' }} />
      {t('license.title')}
    </span>
  );

  const [{ data: subjectContainersData }] = use_SubjectContainersQuery({});
  const [{ data: teachingBlockCurrentStatusData }] = useTeachingBlockCurrentStatusQuery({
    variables: {
      teachingBlockUuids: teachingBlocks?.map((tb) => tb.uuid) ?? [],
    },
    pause: !teachingBlocks,
  });

  const blocks = useMemo(() => {
    const visibleTeachingBlockUuids = new Set(
      (teachingBlockCurrentStatusData?.teachingBlockCurrentStatus ?? [])
        .filter((status) => status.isCurrent || Boolean(status.currentFrom))
        .map((status) => status.teachingBlockUuid),
    );
    const activeTeachingBlocks = teachingBlocks?.filter((tb) => {
      return !tb.draft && visibleTeachingBlockUuids.has(tb.uuid);
    });
    const result: Array<{
      subjectContainerName: string;
      subjectContainerUuid: string;
      grades: string;
      subjectContainerShortName: string;
    }> = [];
    subjectContainersData?.subjectContainers?.forEach((sc) => {
      const teachingBlock = activeTeachingBlocks?.find((tb) => tb.subjectContainerUuid === sc.uuid);
      if (teachingBlock)
        result.push({
          subjectContainerUuid: sc.uuid,
          subjectContainerName: sc.name,
          subjectContainerShortName: sc.shortName,
          grades: sc.grades.join(', '),
        });
    });
    return result;
  }, [teachingBlockCurrentStatusData?.teachingBlockCurrentStatus, teachingBlocks, subjectContainersData]);

  if (variant === 'list') {
    return (
      <div className={styles['subject-container-list']}>
        <div className={styles['sub-headline']}>{t('teachingBlockSubjectContainer.title', { count: 2 })}</div>
        <ul className={styles['block-list']}>
          {blocks.length === 0 && (
            <li className={classNames(styles.row, styles.empty)}>
              <span className={styles.dot} aria-hidden />
              <div className={styles.text}>
                <div className={styles.title}>
                  {epochenLicensed ? t('teachingBlockSubjectContainer.noActive') : licenseHint}
                </div>
              </div>
            </li>
          )}
          {blocks.map((tb) => (
            <li key={tb.subjectContainerUuid} className={styles.row}>
              <span className={styles.dot} aria-hidden />
              <div className={styles.text}>
                <div className={styles.title}>{tb.subjectContainerName}</div>
                {tb.grades && <div className={styles.meta}>{`${t('grades.title', { count: 2 })}: ${tb.grades}`}</div>}
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className={styles['timetable-block-subject-container']}>
      <div className={styles['sub-headline']}>{t('teachingBlockSubjectContainer.title', { count: 2 })}</div>
      <div className={styles['subject-container']}>
        {blocks.length === 0 && (
          <div className={classNames(styles.block, styles['empty-state'])}>{epochenLicensed ? null : licenseHint}</div>
        )}
        {blocks.length > 0 && (
          <>
            <div className={classNames(styles.block, styles['has-block'], styles['subject-container-tooltip'])}>
              {blocks.map((tb, index) => {
                const isLast = index === blocks.length - 1;
                const name = isLast ? tb.subjectContainerShortName : tb.subjectContainerShortName + ', ';
                // add , after tb.subjectContainerName
                return (
                  <Tooltip
                    key={tb.subjectContainerUuid}
                    content={`${tb.subjectContainerName} ${tb.grades ? ' | ' + t('grades.title', { count: 2 }) + ': ' + tb.grades : ''}`}
                  >
                    {name}
                  </Tooltip>
                );
              })}
            </div>
          </>
        )}
        {todayStarted && <TodayMarker percent={todayPercent} withFill showLine={todayInRange} />}
      </div>
    </div>
  );
};
